import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Slider,
  TextField,
  ThemeProvider,
  Typography,
  ScopedCssBaseline,
  createTheme,
} from '@mui/material';
import {
  Build,
  Calculate,
  CheckCircle,
  Email,
  Engineering,
  LocalShipping,
  MonetizationOn,
  QueryStats,
  Speed,
  SupportAgent,
  TireRepair,
  TrendingUp,
  WarningAmber,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { api } from '../services/api';
import { calculateClientSavings, defaultClientCalculatorState } from '../utils/clientSavingsCalculator';
import './ClientCalculatePage.css';

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#e3192a' },
    error: { main: '#e3192a' },
    background: { default: '#f6f8fb', paper: '#ffffff' },
    text: { primary: '#111827', secondary: '#4b5563' },
  },
});

const STEPS = [
  { title: i18n.t('hardcoded.components_admin_ordermanagement.cliente'), description: i18n.t('hardcoded.pages_clientcalculatepage.datos_de_contacto'), icon: <Engineering fontSize="small" /> },
  { title: i18n.t('hardcoded.pages_clientcalculatepage.flotilla'), description: i18n.t('hardcoded.pages_clientcalculatepage.configuracion_de_unidades'), icon: <LocalShipping fontSize="small" /> },
  { title: i18n.t('hardcoded.pages_clientcalculatepage.presion_y_operacion'), description: i18n.t('hardcoded.pages_clientcalculatepage.psi_km_y_consumo'), icon: <Speed fontSize="small" /> },
  { title: i18n.t('hardcoded.pages_clientcalculatepage.llantas'), description: i18n.t('hardcoded.pages_clientcalculatepage.costo_y_vida_util'), icon: <TireRepair fontSize="small" /> },
  { title: i18n.t('hardcoded.pages_clientcalculatepage.talachas'), description: i18n.t('hardcoded.pages_clientcalculatepage.reparaciones_recurrentes'), icon: <Build fontSize="small" /> },
  { title: i18n.t('hardcoded.pages_clientcalculatepage.productividad'), description: i18n.t('hardcoded.pages_clientcalculatepage.impacto_operativo'), icon: <TrendingUp fontSize="small" /> },
  { title: i18n.t('hardcoded.pages_clientcalculatepage.incidentes'), description: i18n.t('hardcoded.pages_clientcalculatepage.riesgos_y_costos_ocultos'), icon: <WarningAmber fontSize="small" /> },
  { title: i18n.t('vehicleDetail.tabs.overview'), description: i18n.t('hardcoded.pages_clientcalculatepage.confirmar_y_enviar'), icon: <CheckCircle fontSize="small" /> },
];

const STEP_REQUIRED_FIELDS = {
  0: ['email'],
  1: ['tractos'],
  2: [
    'recommended_psi',
    'actual_avg_psi',
    'monthly_km_per_unit',
    'diesel_price_per_liter',
  ],
  3: ['average_tire_cost', 'average_tire_life_km'],
};

// Returns dynamic required fields for step 2 based on config type
const getStep2RequiredKmFields = (configType) => {
  if (configType === 'Sencillo') return ['km_per_liter_simple'];
  if (configType === 'Full') return ['km_per_liter_full'];
  return ['km_per_liter_simple', 'km_per_liter_full'];
};

const money = (value, currency) => new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency,
  maximumFractionDigits: 0,
}).format(Number.isFinite(value) ? value : 0);

const percent = (value) => `${(value * 100).toFixed(1)}%`;

const MAX_DROPDOWN_RESULTS = 100;

const normalizeText = (value = '') => value
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const buildCombinedPhone = (phoneCode, phoneNumber) => {
  const code = (phoneCode || '').trim();
  const number = (phoneNumber || '').trim();
  if (!code && !number) return '';
  return `${code}${code && number ? ' ' : ''}${number}`.trim();
};

const SearchableSelect = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  error,
  helperText,
  disabled = false,
  t,
}) => (
  <Autocomplete
    size="small"
    fullWidth
    disabled={disabled}
    options={options}
    value={value}
    onChange={(_, selected) => onChange(selected || null)}
    isOptionEqualToValue={(option, selected) => option.value === selected.value}
    getOptionLabel={(option) => option?.label || ''}
    noOptionsText={t('hardcoded.pages_clientcalculatepage.sin_resultados')}
    filterOptions={(list, { inputValue }) => {
      const query = normalizeText(inputValue || '');
      if (!query) return list.slice(0, MAX_DROPDOWN_RESULTS);
      return list
        .filter((item) => item.searchKey.includes(query))
        .slice(0, MAX_DROPDOWN_RESULTS);
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
      />
    )}
  />
);

const Field = ({
  form,
  errors,
  onInput,
  name,
  label,
  type = 'text',
  prefix,
  suffix,
  helperText,
  ...rest
}) => (
  <TextField
    size="small"
    fullWidth
    name={name}
    label={label}
    type={type}
    value={form[name]}
    onChange={onInput}
    error={Boolean(errors[name])}
    helperText={errors[name] || helperText}
    InputLabelProps={type === 'number' ? { shrink: true } : undefined}
    InputProps={{
      startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined,
      endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined,
    }}
    sx={{
      '& .MuiOutlinedInput-root': {
        bgcolor: '#fff',
        color: '#111827',
        borderRadius: '12px',
        minHeight: type === 'number' ? 46 : undefined,
      },
      '& .MuiInputBase-input': {
        fontSize: '16px',
      },
      '& .MuiInputLabel-root': {
        color: '#374151',
        fontSize: '13px',
      },
      '& .MuiFormHelperText-root': {
        color: errors[name] ? '#d32f2f' : '#6b7280',
        fontSize: '12px',
        marginTop: '4px',
      },
    }}
    {...rest}
  />
);

const ClientCalculatePage = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    ...defaultClientCalculatorState,
    countryId: '',
    stateId: '',
    cityId: '',
    phoneCode: '+52',
    phoneNumber: '',
  });
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [maxVisitedStep, setMaxVisitedStep] = useState(0);
  const [submitState, setSubmitState] = useState({ loading: false, success: '', error: '' });
  const [locationData, setLocationData] = useState({
    countries: [],
    states: [],
    cities: [],
    phoneCodes: [],
  });
  const [locationLoaded, setLocationLoaded] = useState(false);

  useEffect(() => {
    document.title = t('hardcoded.pages_clientcalculatepage.calculadora_de_ahorro_para_flotillas');
    const description = t('hardcoded.pages_clientcalculatepage.calcula_el_ahorro_estimado_de');

    let meta = document.querySelector(t('hardcoded.pages_clientcalculatepage.metanamedescription'));
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = t('hardcoded.components_audit_auditlog.description');
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadLocationData = async () => {
      try {
        const [countriesResp, statesResp, citiesResp, phoneCodesResp] = await Promise.all([
          fetch('/countries-master/json/countries.json'),
          fetch('/countries-master/json/states.json'),
          fetch('/countries-master/json/cities.json'),
          fetch('/countries-master/json/codcountries.json'),
        ]);

        const [countriesJson, statesJson, citiesJson, phoneCodesJson] = await Promise.all([
          countriesResp.json(),
          statesResp.json(),
          citiesResp.json(),
          phoneCodesResp.json(),
        ]);

        if (!isMounted) return;

        const countries = (countriesJson?.countries || []).map((item) => ({
          id: Number(item.id),
          name: item.name,
          searchKey: normalizeText(item.name),
        }));

        const states = (statesJson?.states || []).map((item) => ({
          id: Number(item.id),
          countryId: Number(item.id_country),
          name: item.name,
          searchKey: normalizeText(item.name),
        }));

        const cities = (citiesJson?.cities || []).map((item) => ({
          id: Number(item.id),
          stateId: Number(item.id_state),
          name: item.name,
          searchKey: normalizeText(item.name),
        }));

        const phoneCodes = (phoneCodesJson || []).map((item) => {
          const phoneCode = `+${String(item.phoneCode || '').trim()}`;
          const countryName = item.nameES || item.nameEN || '';
          return {
            value: phoneCode,
            label: `${phoneCode} ${countryName}`.trim(),
            countryName,
            searchKey: normalizeText(`${phoneCode} ${item.nameES || ''} ${item.nameEN || ''}`),
          };
        });

        setLocationData({ countries, states, cities, phoneCodes });
        setLocationLoaded(true);
      } catch (_error) {
        if (!isMounted) return;
        setLocationLoaded(true);
      }
    };

    loadLocationData();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!locationLoaded || locationData.countries.length === 0) return;

    const mexicoCountry = locationData.countries.find((item) => normalizeText(item.name) === 'mexico');

    setForm((prev) => {
      const selectedByName = locationData.countries.find((item) => normalizeText(item.name) === normalizeText(prev.country));
      const country = selectedByName || mexicoCountry || locationData.countries[0] || null;
      if (!country) return prev;

      const phoneForCountry = locationData.phoneCodes.find((item) => normalizeText(item.countryName) === normalizeText(country.name));
      const phoneCode = prev.phoneCode || phoneForCountry?.value || '+52';
      const combinedPhone = buildCombinedPhone(phoneCode, prev.phoneNumber);

      return {
        ...prev,
        country: prev.country || country.name,
        countryId: prev.countryId || String(country.id),
        phoneCode,
        phone: combinedPhone,
      };
    });
  }, [locationLoaded, locationData]);

  const calculatedAutoTires = useMemo(() => {
    const tractos = Number(form.tractos || 0);
    const rem2 = Number(form.trailers_2axle_8tires || 0);
    const rem3 = Number(form.trailers_3axle_12tires || 0);
    return Math.max(0, (tractos * 10) + (rem2 * 8) + (rem3 * 12));
  }, [form.tractos, form.trailers_2axle_8tires, form.trailers_3axle_12tires]);

  const calcInput = useMemo(() => ({
    ...form,
    total_tires: Number(form.total_tires) > 0 ? Number(form.total_tires) : calculatedAutoTires,
    estimated_monthly_tire_cost: Number(form.estimated_monthly_tire_cost) > 0 ? Number(form.estimated_monthly_tire_cost) : 0,
  }), [form, calculatedAutoTires]);

  const results = useMemo(() => calculateClientSavings(calcInput), [calcInput]);

  const annualComparisonData = useMemo(() => {
    const without = results.annual_comparison.without_aps;
    const withAps = results.annual_comparison.with_aps;
    return [
      { label: t('hardcoded.pages_clientcalculatepage.combustible'), without: without.fuel, withAps: withAps.fuel },
      { label: t('hardcoded.pages_clientcalculatepage.llantas'), without: without.tires, withAps: withAps.tires },
      { label: t('hardcoded.pages_clientcalculatepage.talachas'), without: without.repairs, withAps: withAps.repairs },
      { label: t('hardcoded.pages_clientcalculatepage.productividad'), without: without.productivity, withAps: withAps.productivity },
      { label: t('hardcoded.pages_clientcalculatepage.total_anual'), without: without.total, withAps: withAps.total },
    ];
  }, [results]);

  const breakEvenMonth = useMemo(() => {
    const point = results.break_even.find((item) => item.is_break_even);
    return point ? point.month : null;
  }, [results.break_even]);

  const breakEvenProgress = breakEvenMonth ? Math.min((breakEvenMonth / 84) * 100, 100) : 0;

  const onInput = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => {
      const nextValue = type === 'checkbox' ? checked : value;
      const next = { ...prev, [name]: nextValue };

      if (name === 'phoneCode' || name === 'phoneNumber') {
        next.phone = buildCombinedPhone(
          name === 'phoneCode' ? nextValue : prev.phoneCode,
          name === 'phoneNumber' ? nextValue : prev.phoneNumber,
        );
      }

      // Auto-ajustar % Full según tipo de configuración
      if (name === 'configuration_type') {
        if (nextValue === 'Sencillo') next.full_units_percentage = 0;
        else if (nextValue === 'Full') next.full_units_percentage = 100;
      }

      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const countryOptions = useMemo(
    () => locationData.countries.map((item) => ({
      value: item.name,
      label: item.name,
      id: item.id,
      searchKey: item.searchKey,
    })),
    [locationData.countries],
  );

  const selectedCountryOption = useMemo(
    () => countryOptions.find((item) => item.value === form.country) || null,
    [countryOptions, form.country],
  );

  const stateOptions = useMemo(() => {
    if (!form.countryId) return [];
    return locationData.states
      .filter((item) => String(item.countryId) === String(form.countryId))
      .map((item) => ({
        value: item.name,
        label: item.name,
        id: item.id,
        searchKey: item.searchKey,
      }));
  }, [locationData.states, form.countryId]);

  const selectedStateOption = useMemo(
    () => stateOptions.find((item) => item.value === form.state) || null,
    [stateOptions, form.state],
  );

  const cityOptions = useMemo(() => {
    if (!form.stateId) return [];
    return locationData.cities
      .filter((item) => String(item.stateId) === String(form.stateId))
      .map((item) => ({
        value: item.name,
        label: item.name,
        id: item.id,
        searchKey: item.searchKey,
      }));
  }, [locationData.cities, form.stateId]);

  const selectedCityOption = useMemo(
    () => cityOptions.find((item) => item.value === form.city) || null,
    [cityOptions, form.city],
  );

  const phoneCodeOptions = useMemo(
    () => locationData.phoneCodes.map((item, index) => ({
      value: item.value,
      label: item.label,
      id: `${item.value}-${index}`,
      searchKey: item.searchKey,
    })),
    [locationData.phoneCodes],
  );

  const selectedPhoneCodeOption = useMemo(
    () => phoneCodeOptions.find((item) => item.value === form.phoneCode) || null,
    [phoneCodeOptions, form.phoneCode],
  );

  const handleCountryChange = (selected) => {
    if (!selected) {
      setForm((prev) => ({
        ...prev,
        country: '',
        countryId: '',
        state: '',
        stateId: '',
        city: '',
        cityId: '',
      }));
      return;
    }

    const phoneForCountry = locationData.phoneCodes.find(
      (item) => normalizeText(item.countryName) === normalizeText(selected.value),
    );

    setForm((prev) => {
      const nextPhoneCode = phoneForCountry?.value || prev.phoneCode || '+52';
      return {
        ...prev,
        country: selected.value,
        countryId: String(selected.id),
        state: '',
        stateId: '',
        city: '',
        cityId: '',
        phoneCode: nextPhoneCode,
        phone: buildCombinedPhone(nextPhoneCode, prev.phoneNumber),
      };
    });

    setErrors((prev) => ({ ...prev, country: '', state: '', city: '' }));
  };

  const handleStateChange = (selected) => {
    setForm((prev) => ({
      ...prev,
      state: selected?.value || '',
      stateId: selected ? String(selected.id) : '',
      city: '',
      cityId: '',
    }));
    setErrors((prev) => ({ ...prev, state: '', city: '' }));
  };

  const handleCityChange = (selected) => {
    setForm((prev) => ({
      ...prev,
      city: selected?.value || '',
      cityId: selected ? String(selected.id) : '',
    }));
    setErrors((prev) => ({ ...prev, city: '' }));
  };

  const handlePhoneCodeChange = (selected) => {
    const nextCode = selected?.value || '';
    setForm((prev) => ({
      ...prev,
      phoneCode: nextCode,
      phone: buildCombinedPhone(nextCode, prev.phoneNumber),
    }));
  };

  const validateStep = (stepIndex) => {
    const nextErrors = {};
    const baseFields = STEP_REQUIRED_FIELDS[stepIndex] || [];
    const extraFields = stepIndex === 2 ? getStep2RequiredKmFields(form.configuration_type) : [];
    [...baseFields, ...extraFields].forEach((field) => {
      const value = form[field];
      if (value === undefined || value === null || value === '') {
        nextErrors[field] = t('hardcoded.pages_clientcalculatepage.campo_requerido');
      }
    });

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      nextErrors.email = t('hardcoded.pages_clientcalculatepage.correo_invalido');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (!validateStep(currentStep)) return;
    const nextStep = Math.min(currentStep + 1, STEPS.length - 1);
    setCurrentStep(nextStep);
    setMaxVisitedStep((prev) => Math.max(prev, nextStep));
  };

  const goPrev = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

  const jumpToStep = (stepIndex) => {
    if (stepIndex <= maxVisitedStep) {
      setCurrentStep(stepIndex);
    }
  };

  const submit = async () => {
    setSubmitState({ loading: false, success: '', error: '' });

    const allStepErrors = {};
    Object.keys(STEP_REQUIRED_FIELDS).forEach((step) => {
      const stepIndex = Number(step);
      const baseFields = STEP_REQUIRED_FIELDS[stepIndex] || [];
      const extraFields = stepIndex === 2 ? getStep2RequiredKmFields(form.configuration_type) : [];
      [...baseFields, ...extraFields].forEach((field) => {
        const value = form[field];
        if (value === undefined || value === null || value === '') {
          allStepErrors[field] = t('hardcoded.pages_clientcalculatepage.campo_requerido');
        }
      });
    });

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      allStepErrors.email = t('hardcoded.pages_clientcalculatepage.correo_invalido');
    }

    if (Object.keys(allStepErrors).length > 0) {
      setErrors(allStepErrors);
      setSubmitState((prev) => ({ ...prev, error: t('hardcoded.pages_clientcalculatepage.revisa_los_campos_requeridos') }));
      return;
    }

    setSubmitState({ loading: true, success: '', error: '' });

    try {
      await api.public.clientCalculate({
        client: {
          name: form.name?.trim() || t('hardcoded.components_admin_ordermanagement.cliente'),
          company: form.company,
          email: form.email,
          phone: buildCombinedPhone(form.phoneCode, form.phoneNumber),
          phoneCode: form.phoneCode,
          phoneNumber: form.phoneNumber,
          city: form.city,
          state: form.state,
          country: form.country,
          comments: form.comments,
        },
        fleet: {
          tractos: Number(form.tractos),
          trailers_2axle_8tires: Number(form.trailers_2axle_8tires),
          trailers_3axle_12tires: Number(form.trailers_3axle_12tires),
          configuration_type: form.configuration_type,
          full_units_percentage: Number(form.full_units_percentage),
          total_tires: Number(form.total_tires) > 0 ? Number(form.total_tires) : calculatedAutoTires,
          rim_type: form.rim_type,
          steel_rims_count: Number(form.steel_rims_count),
          aluminum_rims_count: Number(form.aluminum_rims_count),
        },
        operation: {
          recommended_psi: Number(form.recommended_psi),
          actual_avg_psi: Number(form.actual_avg_psi),
          monthly_km_per_unit: Number(form.monthly_km_per_unit),
          km_per_liter_simple: Number(form.km_per_liter_simple),
          km_per_liter_full: Number(form.km_per_liter_full),
          diesel_price_per_liter: Number(form.diesel_price_per_liter),
          currency: form.currency,
          mxn_usd_exchange_rate: Number(form.mxn_usd_exchange_rate),
        },
        tires: {
          average_tire_cost: Number(form.average_tire_cost),
          average_tire_life_km: Number(form.average_tire_life_km),
          estimated_monthly_tire_cost: Number(form.estimated_monthly_tire_cost) > 0 ? Number(form.estimated_monthly_tire_cost) : 0,
          monitors_tire_pressure: !!form.monitors_tire_pressure,
        },
        repairs: {
          has_frequent_repairs: !!form.has_frequent_repairs,
          average_km_per_trip: Number(form.average_km_per_trip),
          repair_cost_per_trip: Number(form.repair_cost_per_trip),
          trips_per_month: Number(form.trips_per_month),
        },
        productivity: {
          additional_trips_per_month: Number(form.additional_trips_per_month),
          profit_per_simple_trip: Number(form.profit_per_simple_trip),
          profit_per_full_trip: Number(form.profit_per_full_trip),
        },
        incidents: {
          estimated_monthly_blowouts: Number(form.estimated_monthly_blowouts),
          estimated_monthly_defective_tires: Number(form.estimated_monthly_defective_tires),
          average_blowout_cost: Number(form.average_blowout_cost),
          average_truck_damage_cost: Number(form.average_truck_damage_cost),
          average_third_party_incident_cost: Number(form.average_third_party_incident_cost),
        },
        request_assessment: !!form.request_assessment,
        honeypot: form.honeypot || '',
      });

      setSubmitState({
        loading: false,
        success: t('hardcoded.pages_clientcalculatepage.tu_calculo_fue_enviado_correctamente'),
        error: '',
      });
      setCurrentStep(STEPS.length - 1);
      setMaxVisitedStep(STEPS.length - 1);
    } catch (error) {
      setSubmitState({
        loading: false,
        success: '',
        error: error?.response?.data?.detail || t('hardcoded.pages_clientcalculatepage.no_pudimos_enviar_el_calculo'),
      });
    }
  };

  const renderOptionCards = (name, options) => (
    <div className="client-calc-option-cards">
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          className={t('hardcoded.pages_clientcalculatepage.client-calc-option-card_formnameoptionv', { formnameoptionv: form[name] === option.value ? 'active' : '' })}
          onClick={() => onInput({ target: { name, value: option.value } })}
        >
          <strong>{option.label}</strong>
          <span>{option.description}</span>
        </button>
      ))}
    </div>
  );

  const renderSlider = ({ name, label, min, max, step = 1, unit }) => (
    <div className="client-calc-slider-wrap">
      <div className="client-calc-slider-head">
        <label>{label}</label>
        <strong>{form[name]} {unit}</strong>
      </div>
      <Slider
        value={Number(form[name] || 0)}
        onChange={(_, value) => onInput({ target: { name, value } })}
        min={min}
        max={max}
        step={step}
        color="error"
      />
    </div>
  );

  const renderResultsPanel = (isCompact = false) => (
    <div className={t('hardcoded.pages_clientcalculatepage.client-calc-results-panel_iscompactcompac', { isCompactcompac: isCompact ? 'compact' : '' })}>
      <h3>{t('hardcoded.pages_clientcalculatepage.resultados_estimados_en_vivo')}</h3>
      <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.basado_en_los_datos_ingresados')}</p>

      <div className="client-calc-highlight">
        <strong>{i18n.t('hardcoded.pages_clientcalculatepage.ahorro_anual_estimado')}</strong>
        <div className="client-calc-highlight-value">{money(results.potential_saving_year, form.currency)}</div>
        <span className="client-calc-badge">{i18n.t('hardcoded.pages_clientcalculatepage.estimacion_personalizada')}</span>
      </div>

      <div className="client-calc-kpis">
        <div className="client-calc-kpi"><span>{t('pages_clientcalculatepage.kpi_monthly_savings')}</span><strong>{money(results.potential_saving_month, form.currency)}</strong></div>
        <div className="client-calc-kpi"><span>{t('pages_clientcalculatepage.kpi_payback')}</span><strong>{results.payback_months} {i18n.t('hardcoded.pages_clientcalculatepage.meses')}</strong></div>
        <div className="client-calc-kpi"><span>{i18n.t('hardcoded.pages_clientcalculatepage.roi_estimado')}</span><strong>{percent(results.roi_annual)}</strong></div>
        <div className="client-calc-kpi"><span>{i18n.t('hardcoded.pages_clientcalculatepage.litros_extra_estimados')}</span><strong>{results.extra_liters_month} {i18n.t('hardcoded.pages_clientcalculatepage.lmes')}</strong></div>
        <div className="client-calc-kpi"><span>{i18n.t('hardcoded.pages_clientcalculatepage.costo_oculto_actual')}</span><strong>{money(results.hidden_cost_year, form.currency)}</strong></div>
        <div className="client-calc-kpi"><span>{t('pages_clientcalculatepage.kpi_break_even')}</span><strong>{breakEvenMonth ? t('hardcoded.pages_clientcalculatepage.mes_breakevenmonth', { breakEvenMonth: breakEvenMonth }) : t('common.not_available')}</strong></div>
      </div>

      <div className="client-calc-breakeven-progress">
        <div className="client-calc-breakeven-row">
          <span>{t('hardcoded.pages_clientcalculatepage.progreso_hacia_break-even')}</span>
          <strong>{Math.round(breakEvenProgress)}%</strong>
        </div>
        <LinearProgress variant="determinate" color="error" value={breakEvenProgress} />
      </div>

      <div className="client-calc-mini-breakdown">
        <div><span>{i18n.t('hardcoded.pages_clientcalculatepage.combustible')}</span><strong>{money(results.diesel_overcost_year, form.currency)}</strong></div>
        <div><span>{i18n.t('hardcoded.pages_clientcalculatepage.llantas')}</span><strong>{money(results.tire_wear_loss_year, form.currency)}</strong></div>
        <div><span>{i18n.t('hardcoded.pages_clientcalculatepage.talachas')}</span><strong>{money(results.talacha_year, form.currency)}</strong></div>
        <div><span>{t('hardcoded.pages_clientcalculatepage.productividad')}</span><strong>{money(results.productivity_benefit_year, form.currency)}</strong></div>
      </div>

      {!isCompact && (
        <Button className="client-calc-mt16" variant="contained" color="error" fullWidth startIcon={<Email />} onClick={submit} disabled={submitState.loading}>
          {t('hardcoded.pages_clientcalculatepage.recibir_calculo_por_correo')}</Button>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    if (currentStep === 0) {
      return (
        <section className="client-calc-section">
          <div className="client-calc-section-title">{STEPS[0].icon} {t('hardcoded.pages_clientcalculatepage.paso_1_datos_del_cliente')}</div>
          <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.comparte_tus_datos_para_enviarte')}</p>
          <div className="client-calc-grid-fields">
            <Field form={form} errors={errors} onInput={onInput} name="name" label={t('createUser.success.fullName')} helperText={t('hardcoded.pages_clientcalculatepage.nombre_del_responsable')} />
            <Field form={form} errors={errors} onInput={onInput} name={t('hardcoded.pages_chatanalyticspage.company')} label={t('navbar.company')} />
            <Field form={form} errors={errors} onInput={onInput} name="email" label={t('hardcoded.pages_clientcalculatepage.correo_electronico')} helperText={t('hardcoded.pages_clientcalculatepage.obligatorio')} />

            <div className="client-calc-field-full client-calc-phone-row">
              <SearchableSelect
                label={t('hardcoded.pages_clientcalculatepage.codigo_telefonico')}
                placeholder={t('hardcoded.pages_clientcalculatepage.buscar_codigo')}
                options={phoneCodeOptions}
                value={selectedPhoneCodeOption}
                onChange={handlePhoneCodeChange}
                t={t}
              />
              <Field form={form} errors={errors} onInput={onInput} name="phoneNumber" label={t('hardcoded.pages_checkoutpage.telefono')} />
            </div>

            <SearchableSelect
              label={t('pages_clientcalculatepage.label_country')}
              placeholder={t('hardcoded.pages_clientcalculatepage.buscar_pais')}
              options={countryOptions}
              value={selectedCountryOption}
              onChange={handleCountryChange}
              error={Boolean(errors.country)}
              helperText={errors.country}
              t={t}
            />

            <SearchableSelect
              label={t('gps.table.status')}
              placeholder={t('hardcoded.pages_clientcalculatepage.buscar_estado')}
              options={stateOptions}
              value={selectedStateOption}
              onChange={handleStateChange}
              error={Boolean(errors.state)}
              helperText={errors.state}
              disabled={!form.countryId}
              t={t}
            />

            <SearchableSelect
              label={t('settings.company.city')}
              placeholder={t('hardcoded.pages_clientcalculatepage.buscar_ciudad')}
              options={cityOptions}
              value={selectedCityOption}
              onChange={handleCityChange}
              error={Boolean(errors.city)}
              helperText={errors.city}
              disabled={!form.stateId}
              t={t}
            />

            <Field form={form} errors={errors} onInput={onInput} name={t('hardcoded.pages_clientcalculatepage.comments')} label={t('hardcoded.pages_clientcalculatepage.comentarios')} multiline rows={2} className="client-calc-field-full" />
          </div>
        </section>
      );
    }

    if (currentStep === 1) {
      return (
        <section className="client-calc-section">
          <div className="client-calc-section-title">{STEPS[1].icon} {t('hardcoded.pages_clientcalculatepage.paso_2_configuracion_de_flotilla')}</div>
          <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.define_tu_operacion_para_estimar')}</p>
          {renderOptionCards('configuration_type', [
            { value: 'Sencillo', label: t('hardcoded.pages_clientcalculatepage.sencillo'), description: t('hardcoded.pages_clientcalculatepage.tracto_un_remolque') },
            { value: 'Full', label: t('hardcoded.pages_clientcalculatepage.full'), description: t('hardcoded.pages_clientcalculatepage.tracto_dos_remolques') },
            { value: 'Mixto', label: t('hardcoded.pages_clientcalculatepage.mixto_config'), description: t('hardcoded.pages_clientcalculatepage.combinacion_de_ambos') },
          ])}

          {form.configuration_type === 'Mixto' && (
            <div className="client-calc-slider-grid">
              {renderSlider({ name: 'full_units_percentage', label: t('hardcoded.pages_clientcalculatepage.porcentaje_de_unidades_full'), min: 0, max: 100, unit: '%' })}
            </div>
          )}

          <div className="client-calc-grid-fields">
            <Field form={form} errors={errors} onInput={onInput} name="tractos" label={t('hardcoded.pages_clientcalculatepage.numero_de_tractos')} type="number" />
            <Field form={form} errors={errors} onInput={onInput} name="trailers_2axle_8tires" label={t('hardcoded.pages_clientcalculatepage.remolques_2_ejes_8_llantas')} type="number" />
            <Field form={form} errors={errors} onInput={onInput} name="trailers_3axle_12tires" label={t('hardcoded.pages_clientcalculatepage.remolques_3_ejes_12_llantas')} type="number" />
            <Field form={form} errors={errors} onInput={onInput} name="total_tires" label={t('hardcoded.pages_clientcalculatepage.total_de_llantas_auto_calculatedautotires', { calculatedAutoTires: calculatedAutoTires })} type="number" />
            <Field form={form} errors={errors} onInput={onInput} name="steel_rims_count" label={t('hardcoded.pages_clientcalculatepage.rines_de_acero')} type="number" />
            <Field form={form} errors={errors} onInput={onInput} name="aluminum_rims_count" label={t('hardcoded.pages_clientcalculatepage.rines_de_aluminio')} type="number" />
          </div>

          {renderOptionCards('rim_type', [
            { value: t('hardcoded.pages_clientcalculatepage.acero'), label: t('hardcoded.pages_clientcalculatepage.acero'), description: t('hardcoded.pages_clientcalculatepage.mayor_resistencia') },
            { value: 'Aluminio', label: t('hardcoded.pages_clientcalculatepage.aluminio'), description: t('hardcoded.pages_clientcalculatepage.menor_peso') },
            { value: 'Mixto', label: t('hardcoded.pages_clientcalculatepage.mixto_rim'), description: t('hardcoded.pages_clientcalculatepage.combinacion_operativa') },
          ])}
        </section>
      );
    }

    if (currentStep === 2) {
      return (
        <section className="client-calc-section">
          <div className="client-calc-section-title">{STEPS[2].icon} {i18n.t('hardcoded.pages_clientcalculatepage.paso_3_presion_y_operacion')}</div>
          <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.ajusta_psi_kilometraje_y_rendimiento')}</p>

          <div className="client-calc-slider-grid">
            {renderSlider({ name: 'recommended_psi', label: t('hardcoded.pages_clientcalculatepage.psi_estandar_recomendada'), min: 70, max: 140, unit: 'PSI' })}
            {renderSlider({ name: 'actual_avg_psi', label: t('hardcoded.pages_clientcalculatepage.psi_promedio_real_actual'), min: 60, max: 130, unit: 'PSI' })}
            {renderSlider({ name: 'monthly_km_per_unit', label: t('hardcoded.pages_clientcalculatepage.km_mensuales_por_unidad'), min: 1000, max: 40000, step: 500, unit: 'km' })}
          </div>

          <div className="client-calc-grid-fields">
            {['Sencillo', 'Mixto'].includes(form.configuration_type) && (
              <Field form={form} errors={errors} onInput={onInput} name="km_per_liter_simple" label={t('hardcoded.pages_clientcalculatepage.rendimiento_kml_sencillo')} type="number" />
            )}
            {['Full', 'Mixto'].includes(form.configuration_type) && (
              <Field form={form} errors={errors} onInput={onInput} name="km_per_liter_full" label={t('hardcoded.pages_clientcalculatepage.rendimiento_kml_full')} type="number" />
            )}
            <Field form={form} errors={errors} onInput={onInput} name="diesel_price_per_liter" label={t('hardcoded.pages_clientcalculatepage.precio_diesel_por_litro_sin')} type="number" prefix={form.currency} />
            <TextField
              select
              size="small"
              fullWidth
              name="currency"
              label={t('settings.company.currency')}
              value={form.currency}
              onChange={onInput}
              sx={{ '& .MuiInputBase-root': { bgcolor: '#fff', color: '#111827' } }}
            >
              <MenuItem value="MXN">MXN</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </TextField>
            <Field form={form} errors={errors} onInput={onInput} name="mxn_usd_exchange_rate" label={t('hardcoded.pages_clientcalculatepage.tipo_de_cambio_mxnusd')} type="number" />
          </div>
        </section>
      );
    }

    if (currentStep === 3) {
      return (
        <section className="client-calc-section">
          <div className="client-calc-section-title">{STEPS[3].icon} {i18n.t('hardcoded.pages_clientcalculatepage.paso_4_llantas')}</div>
          <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.modela_costo_de_llantas_y')}</p>

          <div className="client-calc-grid-fields">
            <Field form={form} errors={errors} onInput={onInput} name="average_tire_cost" label={t('hardcoded.pages_clientcalculatepage.costo_promedio_por_llanta')} type="number" prefix={form.currency} />
            <Field form={form} errors={errors} onInput={onInput} name="average_tire_life_km" label={t('hardcoded.pages_clientcalculatepage.duracion_promedio_de_llanta_en')} type="number" suffix="km" />
            <Field form={form} errors={errors} onInput={onInput} name="estimated_monthly_tire_cost" label={t('hardcoded.pages_clientcalculatepage.costo_mensual_estimado_por_llanta')} type="number" prefix={form.currency} />
          </div>

          {renderOptionCards('monitors_tire_pressure', [
            { value: true, label: t('common.yes'), description: t('hardcoded.pages_clientcalculatepage.ya_monitoreamos_presion') },
            { value: false, label: t('common.no'), description: t('hardcoded.pages_clientcalculatepage.no_monitoreamos_presion') },
          ])}
        </section>
      );
    }

    if (currentStep === 4) {
      return (
        <section className="client-calc-section">
          <div className="client-calc-section-title">{STEPS[4].icon} {t('hardcoded.pages_clientcalculatepage.paso_5_talachas_y_reparaciones')}</div>
          <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.incluye_costos_de_reparacion_para')}</p>

          {renderOptionCards('has_frequent_repairs', [
            { value: true, label: t('common.yes'), description: t('hardcoded.pages_clientcalculatepage.tenemos_gastos_frecuentes') },
            { value: false, label: t('common.no'), description: t('hardcoded.pages_clientcalculatepage.gasto_bajo_o_eventual') },
          ])}

          <div className="client-calc-grid-fields">
            <Field form={form} errors={errors} onInput={onInput} name="average_km_per_trip" label={t('hardcoded.pages_clientcalculatepage.km_promedio_por_viaje')} type="number" suffix="km" />
            <Field form={form} errors={errors} onInput={onInput} name="repair_cost_per_trip" label={t('hardcoded.pages_clientcalculatepage.costo_promedio_de_talacha_por')} type="number" prefix={form.currency} />
            <Field form={form} errors={errors} onInput={onInput} name="trips_per_month" label={t('hardcoded.pages_clientcalculatepage.viajes_por_mes')} type="number" />
          </div>
        </section>
      );
    }

    if (currentStep === 5) {
      return (
        <section className="client-calc-section">
          <div className="client-calc-section-title">{STEPS[5].icon} {t('hardcoded.pages_clientcalculatepage.paso_6_productividad')}</div>
          <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.cuantifica_ingresos_extra_al_reducir')}</p>

          <div className="client-calc-grid-fields">
            <Field form={form} errors={errors} onInput={onInput} name="additional_trips_per_month" label={t('hardcoded.pages_clientcalculatepage.viajes_adicionales_por_mes')} type="number" />
            <Field form={form} errors={errors} onInput={onInput} name="profit_per_simple_trip" label={t('hardcoded.pages_clientcalculatepage.utilidad_por_viaje_sencillo')} type="number" prefix={form.currency} />
            <Field form={form} errors={errors} onInput={onInput} name="profit_per_full_trip" label={t('hardcoded.pages_clientcalculatepage.utilidad_por_viaje_full')} type="number" prefix={form.currency} />
          </div>
        </section>
      );
    }

    if (currentStep === 6) {
      return (
        <section className="client-calc-section">
          <div className="client-calc-section-title">{STEPS[6].icon} {i18n.t('hardcoded.pages_clientcalculatepage.paso_7_rescates_e_incidentes')}</div>
          <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.agrega_riesgo_por_blowouts_dano')}</p>

          <div className="client-calc-grid-fields">
            <Field form={form} errors={errors} onInput={onInput} name="estimated_monthly_blowouts" label={t('hardcoded.pages_clientcalculatepage.blowouts_mensuales_estimados')} type="number" />
            <Field form={form} errors={errors} onInput={onInput} name="estimated_monthly_defective_tires" label={t('hardcoded.pages_clientcalculatepage.llantas_defectuosas_mensuales')} type="number" />
            <Field form={form} errors={errors} onInput={onInput} name="average_blowout_cost" label={t('hardcoded.pages_clientcalculatepage.costo_promedio_por_blowout')} type="number" prefix={form.currency} />
            <Field form={form} errors={errors} onInput={onInput} name="average_truck_damage_cost" label={t('hardcoded.pages_clientcalculatepage.costo_promedio_por_dano_al')} type="number" prefix={form.currency} />
            <Field form={form} errors={errors} onInput={onInput} name="average_third_party_incident_cost" label={t('hardcoded.pages_clientcalculatepage.costo_promedio_por_incidente_con')} type="number" prefix={form.currency} />
          </div>

          {renderOptionCards('request_assessment', [
            { value: true, label: t('common.yes'), description: t('hardcoded.pages_clientcalculatepage.quiero_asesoria_comercial') },
            { value: false, label: t('common.no'), description: t('hardcoded.pages_clientcalculatepage.solo_quiero_la_estimacion') },
          ])}
        </section>
      );
    }

    return (
      <section className="client-calc-section">
        <div className="client-calc-section-title">{STEPS[7].icon} {i18n.t('hardcoded.pages_clientcalculatepage.paso_8_resumen_y_envio')}</div>
        <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.esta_estimacion_muestra_el_potencial')}</p>

        <div className="client-calc-summary-grid">
          <div className="client-calc-summary-card">
            <h4>{i18n.t('hardcoded.components_admin_ordermanagement.cliente')}</h4>
            <p><strong>{t('hardcoded.pages_clientcalculatepage.nombre')}</strong> {form.name || '-'}</p>
            <p><strong>{i18n.t('hardcoded.components_admin_ordermanagement.empresa')}</strong> {form.company || '-'}</p>
            <p><strong>Email:</strong> {form.email || '-'}</p>
          </div>
          <div className="client-calc-summary-card">
            <h4>{i18n.t('hardcoded.pages_clientcalculatepage.flotilla')}</h4>
            <p><strong>{i18n.t('hardcoded.pages_clientcalculatepage.tractos')}</strong> {form.tractos}</p>
            <p><strong>{i18n.t('hardcoded.pages_clientcalculatepage.configuracion')}</strong> {form.configuration_type}</p>
            <p><strong>{i18n.t('hardcoded.pages_clientcalculatepage.total_llantas')}</strong> {Number(form.total_tires) > 0 ? form.total_tires : calculatedAutoTires}</p>
          </div>
          <div className="client-calc-summary-card">
            <h4>{i18n.t('hardcoded.pages_clientcalculatepage.resultados_clave')}</h4>
            <p><strong>{i18n.t('hardcoded.pages_clientcalculatepage.ahorro_mensual')}</strong> {money(results.potential_saving_month, form.currency)}</p>
            <p><strong>{i18n.t('hardcoded.pages_clientcalculatepage.ahorro_anual')}</strong> {money(results.potential_saving_year, form.currency)}</p>
            <p><strong>{i18n.t('hardcoded.pages_clientcalculatepage.payback')}</strong> {results.payback_months} {i18n.t('hardcoded.pages_clientcalculatepage.meses')}</p>
          </div>
        </div>

        <div className="client-calc-simple-bars">
          {[
            { label: t('hardcoded.pages_clientcalculatepage.combustible'), value: results.diesel_overcost_year },
            { label: t('hardcoded.pages_clientcalculatepage.llantas'), value: results.tire_wear_loss_year },
            { label: t('hardcoded.pages_clientcalculatepage.talachas'), value: results.talacha_year },
            { label: t('hardcoded.pages_clientcalculatepage.productividad'), value: results.productivity_benefit_year },
          ].map((item) => (
            <div key={item.label} className="client-calc-simple-bar-row">
              <span>{item.label}</span>
              <div className="client-calc-simple-bar-track">
                <div
                  className="client-calc-simple-bar-fill"
                  style={{ width: `${Math.min((item.value / Math.max(results.hidden_cost_year, 1)) * 100, 100)}%` }}
                />
              </div>
              <strong>{money(item.value, form.currency)}</strong>
            </div>
          ))}
        </div>

        <div className="client-calc-chart-wrap">
          <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5, color: '#475569' }}>{t('hardcoded.pages_clientcalculatepage.comparativa_anual_sin_y_con')}</Typography>
          <Box sx={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
              <BarChart data={annualComparisonData} margin={{ top: 16, right: 10, bottom: 20, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5eaf1" />
                <XAxis dataKey="label" stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => money(Number(value), form.currency)} />
                <Bar dataKey="without" fill="#8a97ab" name={t('hardcoded.pages_clientcalculatepage.sin_arsenal_aps')} radius={[6, 6, 0, 0]} />
                <Bar dataKey="withAps" fill="#e3192a" name={t('hardcoded.pages_clientcalculatepage.con_arsenal_aps')} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </div>

        <div className="client-calc-actions">
          <Button variant="contained" color="error" size="large" startIcon={<Email />} onClick={submit} disabled={submitState.loading}>
            {submitState.loading ? t('hardcoded.pages_clientcalculatepage.enviando') : t('hardcoded.pages_clientcalculatepage.enviar_calculo_a_mi_correo')}
          </Button>
          <Button variant="outlined" color="inherit" size="large" startIcon={<SupportAgent />}>
            {t('hardcoded.pages_clientcalculatepage.hablar_con_un_asesor')}</Button>
        </div>
      </section>
    );
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <ScopedCssBaseline>
        <div className="client-calc-page client-calc-light">
          <section className="client-calc-hero">
            <div className="client-calc-hero-content">
              <div className="client-calc-logo-wrap">
                <img src="/logo.png" alt={t('hardcoded.pages_adoptioncampaignspage.arsenal_aps')} className="client-calc-logo" />
              </div>
              <h1 className="client-calc-title">{t('hardcoded.pages_clientcalculatepage.calcula_cuanto_puede_ahorrar_tu')}</h1>
              <p className="client-calc-subtitle">
                {t('hardcoded.pages_clientcalculatepage.descubre_en_minutos_el_impacto')}</p>
              <div className="client-calc-hero-actions">
                <Button variant="contained" color="error" size="large" startIcon={<Calculate />}>{t('hardcoded.pages_clientcalculatepage.empezar_calculo')}</Button>
                <Button variant="outlined" color="inherit" size="large" startIcon={<SupportAgent />}>{t('hardcoded.pages_clientcalculatepage.hablar_con_un_asesor')}</Button>
              </div>
              <div className="client-calc-trust">
                <span><CheckCircle sx={{ fontSize: 14, color: '#e3192a' }} /> {i18n.t('hardcoded.pages_clientcalculatepage.estimacion_personalizada')}</span>
                <span><QueryStats sx={{ fontSize: 14, color: '#e3192a' }} /> {i18n.t('hardcoded.pages_clientcalculatepage.payback_calculado')}</span>
                <span><Email sx={{ fontSize: 14, color: '#e3192a' }} /> {t('hardcoded.pages_clientcalculatepage.envio_por_correo')}</span>
              </div>
            </div>
          </section>

          <main className="client-calc-main">
            <div className="client-calc-grid">
              <div className="client-calc-panel client-calc-form">
                <h3>{i18n.t('hardcoded.pages_clientcalculatepage.calculadora_interactiva')}</h3>
                <p className="client-calc-section-note">{t('hardcoded.pages_clientcalculatepage.completa_cada_paso_para_generar')}</p>

                <div className="client-calc-progress-wrap">
                  <div className="client-calc-progress-head">
                    <span>{i18n.t('hardcoded.pages_clientcalculatepage.paso')}{currentStep + 1} de {STEPS.length}</span>
                    <strong>{Math.round(((currentStep + 1) / STEPS.length) * 100)}%</strong>
                  </div>
                  <LinearProgress variant="determinate" value={((currentStep + 1) / STEPS.length) * 100} color="error" />
                  <div className="client-calc-steps">
                    {STEPS.map((step, index) => (
                      <button
                        key={step.title}
                        type="button"
                        className={t('hardcoded.pages_clientcalculatepage.client-calc-step_indexcurrentste_indexmaxvisited', { indexcurrentSte: index === currentStep ? 'active' : '', indexmaxVisited: index <= maxVisitedStep ? 'visited' : '' })}
                        onClick={() => jumpToStep(index)}
                        disabled={index > maxVisitedStep}
                      >
                        <span className="client-calc-step-index">{index + 1}</span>
                        <span className="client-calc-step-body">
                          <strong>{step.title}</strong>
                          <small>{step.description}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {renderCurrentStep()}

                <div className="client-calc-actions">
                  <Button variant="outlined" color="inherit" onClick={goPrev} disabled={currentStep === 0}>{i18n.t('hardcoded.components_tutorial_tutorialtooltip.anterior')}</Button>
                  {currentStep < STEPS.length - 1 ? (
                    <Button variant="contained" color="error" onClick={goNext}>{i18n.t('hardcoded.components_auth_register.siguiente')}</Button>
                  ) : (
                    <Button variant="contained" color="error" onClick={submit} disabled={submitState.loading}>
                      {submitState.loading ? t('hardcoded.pages_clientcalculatepage.enviando') : t('hardcoded.pages_clientcalculatepage.enviar_calculo_a_mi_correo')}
                    </Button>
                  )}
                </div>

                <input
                  type="text"
                  name="honeypot"
                  autoComplete="off"
                  tabIndex={-1}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
                  value={form.honeypot}
                  onChange={onInput}
                />

                {submitState.success && <Alert severity="success" className="client-calc-alert">{submitState.success}</Alert>}
                {submitState.error && <Alert severity="error" className="client-calc-alert">{submitState.error}</Alert>}

                <div className="client-calc-mobile-results">
                  {renderResultsPanel(true)}
                </div>
              </div>

              <div className="client-calc-panel client-calc-results">
                {renderResultsPanel(false)}
              </div>
            </div>
          </main>

          <section className="client-calc-benefits">
            <article className="client-calc-benefit">
              <TireRepair color="error" />
              <h4>{t('hardcoded.pages_clientcalculatepage.mayor_vida_util_de_llantas')}</h4>
              <p>{t('hardcoded.pages_clientcalculatepage.hasta_30_mas_de_vida')}</p>
            </article>
            <article className="client-calc-benefit">
              <LocalShipping color="error" />
              <h4>{t('hardcoded.pages_clientcalculatepage.mayor_eficiencia_de_combustible')}</h4>
              <p>{t('hardcoded.pages_clientcalculatepage.ahorros_medibles_al_reducir_resistencia')}</p>
            </article>
            <article className="client-calc-benefit">
              <QueryStats color="error" />
              <h4>{t('hardcoded.pages_clientcalculatepage.decisiones_basadas_en_datos')}</h4>
              <p>{t('hardcoded.pages_clientcalculatepage.alertas_y_analitica_para_actuar')}</p>
            </article>
            <article className="client-calc-benefit">
              <MonetizationOn color="error" />
              <h4>{t('hardcoded.pages_clientcalculatepage.control_de_costos_de_flotilla')}</h4>
              <p>{t('hardcoded.pages_clientcalculatepage.visibilidad_para_reducir_costos_operativos')}</p>
            </article>
          </section>

          <section className="client-calc-final-cta">
            <div className="client-calc-final-box">
              <div>
                <h3>{t('hardcoded.pages_clientcalculatepage.listo_para_optimizar_tu_flotilla')}</h3>
                <p>{t('hardcoded.pages_clientcalculatepage.un_asesor_de_arsenal_aps')}</p>
              </div>
              <Button variant="contained" color="error" size="large" startIcon={<SupportAgent />}>{t('hardcoded.pages_clientcalculatepage.hablar_con_un_asesor')}</Button>
            </div>
          </section>
        </div>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
};

export default ClientCalculatePage;
